import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useI18n } from "../../i18n";

const Shipping = () => {
  const { lang, t } = useI18n();
  return (
    <>
      <Header />
      
      <section className="pt-36 pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="rise font-display text-5xl md:text-6xl leading-[1.05] mb-4 text-ink">
            {lang === "en" ? "Shipping & Delivery Policy" : t("footer.legal.shipping")}
          </h1>
          {lang !== "en" && (
            <p className="mb-4 rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink-2">{t("legal.notice")}</p>
          )}
          <p className="text-ink-3 text-sm mb-12 pb-8 border-b border-line">
            Last Updated: December 2024
          </p>

          <div className="legal" dir="ltr" lang="en">
            <section>
              <h2>1. Nature of Services</h2>
              <p className="text-base leading-relaxed mb-4">
                Optivra Tech provides digital services and solutions. Our offerings are entirely digital in nature and do not involve physical products or shipping. This policy outlines how we deliver our digital services to you.
              </p>
            </section>

            <section>
              <h2>2. Digital Delivery Method</h2>
              <p className="text-base leading-relaxed mb-4">
                All services are delivered digitally through the following methods:
              </p>
              <ul className="space-y-2 ml-4">
                <li><strong>Email:</strong> Project files, credentials, and documentation sent to your registered email</li>
                <li><strong>Cloud Storage:</strong> Large files shared via secure cloud platforms (Google Drive, Dropbox, etc.)</li>
                <li><strong>GitHub/Repository:</strong> Source code delivered through private repositories</li>
                <li><strong>Live Deployment:</strong> Web applications deployed on your hosting or our staging servers</li>
                <li><strong>Video Conferencing:</strong> Training and handover sessions conducted online</li>
              </ul>
            </section>

            <section>
              <h2>3. Delivery Timeline</h2>
              
              <h3>3.1 Project-Based Delivery</h3>
              <p className="text-base leading-relaxed mb-4">
                Delivery timelines depend on the service type and project scope:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li><strong>Static Websites:</strong> 1-2 weeks</li>
                <li><strong>Dynamic Websites:</strong> 3-4 weeks</li>
                <li><strong>Custom Web Applications:</strong> 6-10 weeks</li>
                <li><strong>AI Automation Systems:</strong> 2-6 weeks</li>
                <li><strong>Python Automation Scripts:</strong> 3-14 days</li>
                <li><strong>AI Video Systems:</strong> 2-5 weeks</li>
              </ul>
              <p className="text-base leading-relaxed">
                Exact timelines are specified in your project agreement and may vary based on project complexity and requirements.
              </p>

              <h3>3.2 Milestone-Based Delivery</h3>
              <p className="text-base leading-relaxed mb-4">
                For larger projects, we deliver in phases or milestones:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Each milestone has a specific delivery date</li>
                <li>You receive deliverables for review and approval</li>
                <li>Final delivery occurs after all milestones are complete</li>
              </ul>
            </section>

            <section>
              <h2>4. Delivery Process</h2>
              
              <h3>Step 1: Project Completion</h3>
              <p className="text-base leading-relaxed mb-4">
                Once development is complete, we conduct internal testing and quality checks.
              </p>

              <h3>Step 2: Client Review</h3>
              <p className="text-base leading-relaxed mb-4">
                Deliverables are shared with you for review on a staging environment or test platform.
              </p>

              <h3>Step 3: Revisions (if needed)</h3>
              <p className="text-base leading-relaxed mb-4">
                We implement approved revisions as per the project agreement.
              </p>

              <h3>Step 4: Final Delivery</h3>
              <p className="text-base leading-relaxed mb-4">
                Upon approval and final payment, we deliver:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Complete source code</li>
                <li>Database exports (if applicable)</li>
                <li>Documentation and setup guides</li>
                <li>Login credentials</li>
                <li>Deployment assistance</li>
              </ul>

              <h3>Step 5: Handover & Training</h3>
              <p className="text-base leading-relaxed">
                We conduct handover sessions to ensure you can manage and maintain the delivered solution.
              </p>
            </section>

            <section>
              <h2>5. What You Receive</h2>
              
              <h3>5.1 For Website Development</h3>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Complete website source code</li>
                <li>Database structure and data</li>
                <li>Admin panel access (if applicable)</li>
                <li>Hosting setup assistance</li>
                <li>Technical documentation</li>
                <li>Post-launch support period</li>
              </ul>

              <h3>5.2 For AI/Automation Solutions</h3>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Complete automation scripts/system</li>
                <li>Configuration files</li>
                <li>Integration guides</li>
                <li>Training materials</li>
                <li>Support documentation</li>
              </ul>

              <h3>5.3 For Consulting Services</h3>
              <ul className="space-y-2 ml-4">
                <li>Consultation session recordings</li>
                <li>Strategy documents</li>
                <li>Technical recommendations</li>
                <li>Implementation roadmaps</li>
              </ul>
            </section>

            <section>
              <h2>6. Delivery Locations</h2>
              <p className="text-base leading-relaxed mb-4">
                Since our services are digital, we can deliver to clients anywhere in the world. There are no geographical restrictions or shipping charges.
              </p>
              <p className="text-base leading-relaxed">
                We primarily serve clients in India but welcome international projects. All communications and deliveries are conducted remotely via digital channels.
              </p>
            </section>

            <section>
              <h2>7. Delivery Delays</h2>
              <p className="text-base leading-relaxed mb-4">
                Timelines are estimates and may be affected by:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Delayed client feedback or approvals</li>
                <li>Changes in project scope (scope creep)</li>
                <li>Unavailability of required third-party services or APIs</li>
                <li>Technical challenges or complexities</li>
                <li>Force majeure events (natural disasters, pandemics, etc.)</li>
              </ul>
              <p className="text-base leading-relaxed">
                We will promptly communicate any anticipated delays and work to minimize their impact on your project.
              </p>
            </section>

            <section>
              <h2>8. Secure Delivery</h2>
              <p className="text-base leading-relaxed mb-4">
                We take security seriously during delivery:
              </p>
              <ul className="space-y-2 ml-4">
                <li>All files are transferred through secure, encrypted channels</li>
                <li>Passwords and credentials shared via secure password managers</li>
                <li>Private GitHub repositories for source code</li>
                <li>SSL-secured cloud storage links</li>
                <li>Confidentiality maintained throughout delivery process</li>
              </ul>
            </section>

            <section>
              <h2>9. Post-Delivery Support</h2>
              <p className="text-base leading-relaxed mb-4">
                After delivery, we provide:
              </p>
              <ul className="space-y-2 ml-4">
                <li><strong>Bug Fixes:</strong> 30-60 days of bug fix support</li>
                <li><strong>Technical Assistance:</strong> Help with deployment and setup</li>
                <li><strong>Documentation:</strong> Comprehensive guides for maintenance</li>
                <li><strong>Training:</strong> Sessions to help you manage the solution</li>
              </ul>
            </section>

            <section>
              <h2>10. Delivery Confirmation</h2>
              <p className="text-base leading-relaxed mb-4">
                Upon delivery, we request:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Confirmation of receipt of all deliverables</li>
                <li>Verification that files are accessible and complete</li>
                <li>Sign-off on project completion</li>
              </ul>
            </section>

            <section>
              <h2>11. Re-Delivery</h2>
              <p className="text-base leading-relaxed mb-4">
                If you experience issues accessing delivered files:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Contact us immediately at founder@optivratech.com</li>
                <li>We will resend files through alternative secure channels</li>
                <li>No additional charges for re-delivery of same content</li>
              </ul>
            </section>

            <section>
              <h2>12. Contact for Delivery Queries</h2>
              <p className="text-base leading-relaxed mb-2">
                For questions about delivery timelines or process:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Optivra Tech</strong></p>
                <p className="text-base mb-2">GST: 08BNOPS7805R1ZT</p>
                <p className="text-base mb-2">Address: C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2">Email: founder@optivratech.com</p>
                <p className="text-base mb-2">Phone: +91 86198 38718</p>
                <p className="text-base">Business Hours: Mon-Sat, 9 AM - 7 PM IST</p>
              </div>
            </section>

            <section>
              <h2>13. Important Note</h2>
              <div className="rounded-lg bg-brand-soft border border-brand/20 p-6">
                <p className="text-base leading-relaxed">
                  <strong>No Physical Shipping:</strong> Optivra Tech provides exclusively digital services. There are no physical products, no shipping charges, and no physical delivery addresses required. All services are delivered electronically to your email or through secure digital channels.
                </p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Shipping;
